import {} from 'Components/OnboardMerchant/styles';

import { MerchantFileUploadDocType } from 'APIs/files/files.types';
import { MerchantPersonRole } from 'APIs/merchant.persons/merchant.persons.types';
import Message from 'Components/Message';
import Modal from 'Components/Modal/Modal';
import { countryStatesRegions, merchantDocsAllowedFileTypes, merchantPersonRoles } from 'Constants/Constants';
import { countries } from 'Constants/Constants';
import { Label } from 'Constants/styles';
import { ChangeEvent, FunctionComponent, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FaHourglassHalf } from 'react-icons/fa';
import { FiUpload } from 'react-icons/fi';
import { MdDelete } from 'react-icons/md';
import Select from 'react-select';

import { yupResolver } from '@hookform/resolvers/yup';

import { FileWrapper, FormControl, FormItem, FormWrapper, MultiColRow, SubTitle, VInput } from './styles';
import { PersonFormProps, defaultIdType } from './types';
import validationSchema from './validation.schema';

type Props = {
  personFormProps: PersonFormProps | null;
  modalErrorMsg: string;
  isModalLoading: boolean;
  savePersonHendler: (formData: PersonFormProps) => void;
  setShowModal: (showModal: boolean) => void;
  handleFileUploads: (file: File, docType: MerchantFileUploadDocType) => Promise<string | null>;
};

const PersonModal = ({
  personFormProps,
  modalErrorMsg,
  isModalLoading,
  savePersonHendler,
  setShowModal,
  handleFileUploads,
}: Props): ReactElement => {
  const UploadIcon = FiUpload as FunctionComponent;
  const RemoveFile = MdDelete as FunctionComponent;
  const HourGlassIcon = FaHourglassHalf as FunctionComponent;

  const [isNewPerson, setisNewPerson] = useState<boolean>(true);
  const [hideError, setHideError] = useState<boolean>(false);
  const [currentlyUploading, setCurrentlyUploading] = useState<Array<string>>([]);

  const errorContainerRef = useRef<HTMLDivElement>(null);

  const allowedFileTypesStr = useMemo(() => {
    return merchantDocsAllowedFileTypes.map((fT) => `.${fT}`).join(', ');
  }, []);

  // Calculate date 18 years ago
  const currentDate = new Date();
  const eighteenYearsAgo = new Date(currentDate);
  eighteenYearsAgo.setFullYear(currentDate.getFullYear() - 18);
  const defaultBOD = eighteenYearsAgo.toISOString().split('T')[0];

  const defaultValues: PersonFormProps = {
    ownerId: null,
    title: '',
    roles: [],
    givenName: '',
    familyName: '',
    dateOfBirth: defaultBOD,
    address: {
      streetNumber: '',
      streetName: '',
      streetType: '',
      suburb: '',
      postalCode: '',
      country: 'AU',
    },
    emailAddress: '',
    phoneNumber: '',
    verificationDocs: {
      idType: defaultIdType,
    },
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    trigger,
    control,
    formState: { errors },
  } = useForm<PersonFormProps>({
    resolver: yupResolver(validationSchema),
    defaultValues: personFormProps ?? defaultValues,
  });

  const onSubmit = useCallback(
    async (formData) => {
      console.log('called onSubmit');
      savePersonHendler(formData);
    },
    [savePersonHendler],
  );

  const handleFormSubmit = useCallback(() => {
    return handleSubmit(onSubmit);
  }, [handleSubmit, onSubmit]);

  const handleCancel = () => {
    setShowModal(false);
  };

  const fileUploadSelected = useCallback(
    async (event: ChangeEvent<HTMLInputElement>, dType: string, dKey: string, uploadDKey: string) => {
      event.preventDefault();
      const input = event.target;
      const file = input.files && input.files.length > 0 ? input.files[0] : null;

      if (!file) return;

      const dataKey = dKey as keyof PersonFormProps;
      const docType = dType as MerchantFileUploadDocType;
      const uploadDataKey = uploadDKey as keyof PersonFormProps;

      setValue(uploadDataKey, file as any);
      const isValid = await trigger(uploadDataKey);

      if (!isValid) return;

      setCurrentlyUploading((prev) => [...prev, dataKey]);
      const fileId = await handleFileUploads(file, docType);
      setCurrentlyUploading((prev) => prev.filter((dk) => dk !== dataKey));

      if (fileId) {
        setValue(dataKey, fileId);
        trigger(dataKey);
      } else {
        setError(uploadDataKey, {
          type: 'manual',
          message: 'File upload failed',
        });
      }
    },
    [handleFileUploads, setError, setValue, trigger],
  );

  const removeFile = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, dataKey: string, fileId?: string) => {
      e.preventDefault();
      setValue(dataKey as keyof PersonFormProps, undefined);
    },
    [setValue],
  );

  useEffect(() => {
    setisNewPerson(!(personFormProps && personFormProps.ownerId));
  }, [personFormProps]);

  useEffect(() => {
    if (modalErrorMsg && errorContainerRef && errorContainerRef.current) {
      setHideError(false);
      errorContainerRef.current.scrollIntoView();
    }
  }, [modalErrorMsg]);

  const docBackFileId = watch('verificationDocs.docBackFileId');
  const docFrontFileId = watch('verificationDocs.docFrontFileId');
  const idType = watch('verificationDocs.idType');
  const driverLicenceCountry = watch('driverLicence.country');
  const addressCountry = watch('address.country');

  const getAllErrors = () => {
    return Object.entries(errors).flatMap(([field, error]) => {
      if (field === 'verificationDocs' && error) {
        return Object.entries(error).map(([subField, subError]) => ({
          field: `verificationDocs.${subField}`,
          message: subError.message,
        }));
      }
      if (field === 'driverLicence' && error) {
        return Object.entries(error).map(([subField, subError]) => ({
          field: `driverLicence.${subField}`,
          message: subError.message,
        }));
      }
      return [{ field, message: error.message }];
    });
  };

  return (
    <Modal
      title={isNewPerson ? 'Add new beneficial owner' : 'Edit beneficial owner'}
      cancelBtnText="Cancel"
      confirmBtnText={isNewPerson ? 'Add' : 'Save'}
      handleCancel={handleCancel}
      handleSubmit={handleFormSubmit()}
      disableConfirmBtn={isModalLoading || currentlyUploading.length > 0}
      isLoading={isModalLoading}
      large={false}
      testId={'merchant-person-modal'}
    >
      <div ref={errorContainerRef}>
        {modalErrorMsg.length > 0 && !hideError && (
          <Message
            className="mt-2 mb-0"
            success={false}
            description={modalErrorMsg}
            handleClose={(e) => {
              e.preventDefault();
              setHideError(true);
            }}
          />
        )}
      </div>
      {/* Display all errors in a summary */}
      {Object.keys(errors).length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <h3>All Validation Errors:</h3>
          <ul>
            {getAllErrors().map((error, index) => (
              <li key={index} style={{ color: 'red' }}>
                {error.field}: {error.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      <FormWrapper>
        <FormItem>
          <Label>Title*</Label>
          <VInput {...register('title')} isValid={!errors.title} />
          {errors.title && <span className="error-msg">{errors.title.message}</span>}
        </FormItem>
        <MultiColRow>
          <FormItem>
            <Label>first name*</Label>
            <VInput {...register('givenName')} isValid={!errors.givenName} />
            {errors.givenName && <span className="error-msg">{errors.givenName.message}</span>}
          </FormItem>
          <FormItem>
            <Label>Last name*</Label>
            <VInput {...register('familyName')} isValid={!errors.familyName} />
            {errors.familyName && <span className="error-msg">{errors.familyName.message}</span>}
          </FormItem>
        </MultiColRow>
        <FormItem>
          <Label>Role(s)*</Label>
          <Controller
            name="roles"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                isMulti
                className={errors.roles ? 'react-select-container error' : 'react-select-container'}
                placeholder="Select role(s)"
                options={merchantPersonRoles}
                onChange={(selectedOption: MerchantPersonRole) => {
                  console.log(selectedOption);
                  return field.onChange(selectedOption);
                }}
              />
            )}
          />
          {errors.roles && <span className="error-msg">{errors.roles.message}</span>}
        </FormItem>
        <FormItem>
          <Label>Email address*</Label>
          <VInput {...register('emailAddress')} isValid={!errors.emailAddress} />
          {errors.emailAddress && <span className="error-msg">{errors.emailAddress.message}</span>}
        </FormItem>
        <MultiColRow>
          <FormItem>
            <Label>Date of birth*</Label>
            <VInput {...register('dateOfBirth')} type="date" isValid={!errors.dateOfBirth} />
            {errors.dateOfBirth && <span className="error-msg">{errors.dateOfBirth.message}</span>}
          </FormItem>
          <FormItem>
            <Label>Phone*</Label>
            <VInput {...register('phoneNumber')} isValid={!errors.phoneNumber} />
            {errors.phoneNumber && <span className="error-msg">{errors.phoneNumber.message}</span>}
          </FormItem>
        </MultiColRow>

        <SubTitle>Address</SubTitle>

        <MultiColRow>
          <FormItem className="col">
            <Label>Unit Number</Label>
            <VInput {...register('address.unitNumber')} />
            {errors.address?.unitNumber && <span className="error-msg">{errors.address.unitNumber.message}</span>}
          </FormItem>
          <FormItem className="col">
            <Label>Street Number*</Label>
            <VInput {...register('address.streetNumber')} />
            {errors.address?.streetNumber && <span className="error-msg">{errors.address.streetNumber.message}</span>}
          </FormItem>
        </MultiColRow>
        <MultiColRow>
          <FormItem>
            <Label>Street Name*</Label>
            <VInput {...register('address.streetName')} />
            {errors.address?.streetName && <span className="error-msg">{errors.address.streetName.message}</span>}
          </FormItem>
          <FormItem className="col">
            <Label>Street Type*</Label>
            <VInput {...register('address.streetType')} />
            {errors.address?.streetType && <span className="error-msg">{errors.address.streetType.message}</span>}
          </FormItem>
        </MultiColRow>
        <MultiColRow>
          <FormItem className="col">
            <Label>Suburb</Label>
            <VInput {...register('address.suburb')} />
            {errors.address?.suburb && <span className="error-msg">{errors.address.suburb.message}</span>}
          </FormItem>
          <FormItem className="col">
            <Label>Postal Code*</Label>
            <VInput {...register('address.postalCode')} type="number" />
            {errors.address?.postalCode && <span className="error-msg">{errors.address.postalCode.message}</span>}
          </FormItem>
        </MultiColRow>
        <MultiColRow>
          {['AU', 'US'].includes(addressCountry) ? (
            <FormItem className="col">
              <Label>State*</Label>
              <FormControl {...register('address.state')}>
                <option value="">Select state</option>
                {countryStatesRegions[addressCountry as keyof typeof countryStatesRegions].map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </FormControl>
              {errors.address?.state && <span className="error-msg">{errors.address.state.message}</span>}
            </FormItem>
          ) : (
            <FormItem className="col">
              <Label>City*</Label>
              {addressCountry === 'NZ' ? (
                <FormControl {...register('address.city')}>
                  <option value="">Select city</option>
                  {countryStatesRegions.NZ.map((city) => (
                    <option key={city.value} value={city.value}>
                      {city.label}
                    </option>
                  ))}
                </FormControl>
              ) : (
                <VInput {...register('address.city')} />
              )}
              {errors.address?.city && <span className="error-msg">{errors.address.city.message}</span>}
            </FormItem>
          )}
          <FormItem className="col">
            <Label>Country</Label>
            <FormControl {...register('address.country')}>
              {countries.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </FormControl>
            {errors.address?.country && <span className="error-msg">{errors.address.country.message}</span>}
          </FormItem>
        </MultiColRow>

        <SubTitle>Verification document</SubTitle>

        <FormItem className="col">
          <Label>Document type</Label>
          <FormControl {...register('verificationDocs.idType')}>
            <option value="DriverLicence">Driver licence</option>
            <option value="Passport">Passport</option>
          </FormControl>
          {errors.address?.country && <span className="error-msg">{errors.address.country.message}</span>}
        </FormItem>

        <MultiColRow>
          <FormItem className="col">
            <Label>Document front*</Label>
            <FileWrapper>
              {docFrontFileId ? (
                <>
                  <div>{docFrontFileId}</div>
                  <button
                    type="button"
                    className="fileAction"
                    onClick={(e) => removeFile(e, 'verificationDocs.docFrontFileId')}
                  >
                    <RemoveFile />
                  </button>
                </>
              ) : (
                <span className="pending">-- Pending --</span>
              )}
            </FileWrapper>
            {errors.verificationDocs?.docFrontFileId && (
              <div>
                <span className="error-msg">{errors.verificationDocs.docFrontFileId.message}</span>
              </div>
            )}
          </FormItem>
          <FormItem className="col">
            {currentlyUploading.includes('verificationDocs.docFrontFileId') ? (
              <label className="uploadButton">
                <HourGlassIcon />
              </label>
            ) : (
              <label className="uploadButton" htmlFor="verif-docs-front-upload">
                <UploadIcon />
              </label>
            )}
            <input
              className="fileUpload"
              id="verif-docs-front-upload"
              type="file"
              accept={allowedFileTypesStr}
              {...(register('verificationDocs.docFrontFile'),
              {
                onChange: (e) =>
                  fileUploadSelected(
                    e,
                    'PersonVerificationDocumentFront',
                    'verificationDocs.docFrontFileId',
                    'verificationDocs.docFrontFile',
                  ),
              })}
            />
            {errors.verificationDocs?.docFrontFile && (
              <span className="error-msg">{errors.verificationDocs.docFrontFile.message}</span>
            )}
          </FormItem>
        </MultiColRow>
        {idType === 'DriverLicence' && (
          <MultiColRow>
            <FormItem className="col">
              <Label>Document back{idType === 'DriverLicence' ? '*' : ''}</Label>
              <FileWrapper>
                {docBackFileId ? (
                  <>
                    <div>{docBackFileId}</div>
                    <button
                      type="button"
                      className="fileAction"
                      onClick={(e) => removeFile(e, 'verificationDocs.docBackFileId')}
                    >
                      <RemoveFile />
                    </button>
                  </>
                ) : (
                  <span className="pending">-- Pending --</span>
                )}
              </FileWrapper>
              {errors.verificationDocs?.docBackFileId && (
                <div>
                  <span className="error-msg">{errors.verificationDocs.docBackFileId.message}</span>
                </div>
              )}
            </FormItem>
            <FormItem className="col">
              {currentlyUploading.includes('verificationDocs.docBackFileId') ? (
                <label className="uploadButton">
                  <HourGlassIcon />
                </label>
              ) : (
                <label className="uploadButton" htmlFor="verif-docs-back-upload">
                  <UploadIcon />
                </label>
              )}
              <input
                className="fileUpload"
                id="verif-docs-back-upload"
                type="file"
                accept={allowedFileTypesStr}
                {...(register('verificationDocs.docBackFileId'),
                {
                  onChange: (e) =>
                    fileUploadSelected(
                      e,
                      'PersonVerificationDocumentBack',
                      'verificationDocs.docBackFileId',
                      'verificationDocs.docBackFile',
                    ),
                })}
              />
              {errors.verificationDocs?.docBackFile && (
                <span className="error-msg">{errors.verificationDocs.docBackFile.message}</span>
              )}
            </FormItem>
          </MultiColRow>
        )}

        {idType === 'DriverLicence' && (
          <>
            <FormItem className="col">
              <Label>Country</Label>
              <FormControl {...register('driverLicence.country')}>
                {countries.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </FormControl>
              {errors.driverLicence?.country && (
                <span className="error-msg">{errors.driverLicence.country.message}</span>
              )}
            </FormItem>
            <MultiColRow>
              <FormItem>
                <Label>Licence number*</Label>
                <VInput
                  {...register('driverLicence.driverLicenceNumber')}
                  isValid={!errors.driverLicence?.driverLicenceNumber}
                />
                {errors.driverLicence?.driverLicenceNumber && (
                  <span className="error-msg">{errors.driverLicence.driverLicenceNumber.message}</span>
                )}
              </FormItem>
              {driverLicenceCountry === 'NZ' && (
                <FormItem>
                  <Label>Version number*</Label>
                  <VInput {...register('driverLicence.versionNumber')} isValid={!errors.driverLicence?.versionNumber} />
                  {errors.driverLicence?.versionNumber && (
                    <span className="error-msg">{errors.driverLicence.versionNumber.message}</span>
                  )}
                </FormItem>
              )}
              {driverLicenceCountry !== 'NZ' && (
                <FormItem>
                  <Label>Document number*</Label>
                  <VInput
                    {...register('driverLicence.documentNumber')}
                    isValid={!errors.driverLicence?.documentNumber}
                  />
                  {errors.driverLicence?.documentNumber && (
                    <span className="error-msg">{errors.driverLicence.documentNumber.message}</span>
                  )}
                </FormItem>
              )}
            </MultiColRow>

            {driverLicenceCountry !== 'NZ' && (
              <FormItem>
                <Label>State</Label>
                <VInput {...register('driverLicence.state')} isValid={!errors.driverLicence?.state} />
                {errors.driverLicence?.state && <span className="error-msg">{errors.driverLicence.state.message}</span>}
              </FormItem>
            )}
          </>
        )}
        {idType === 'Passport' && (
          <>
            <FormItem className="col">
              <Label>Country*</Label>
              <FormControl {...register('passport.country')}>
                {countries.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </FormControl>
              {errors.passport?.country && <span className="error-msg">{errors.passport.country.message}</span>}
            </FormItem>
            <MultiColRow>
              <FormItem>
                <Label>Passport number*</Label>
                <VInput {...register('passport.passportNumber')} isValid={!errors.passport?.passportNumber} />
                {errors.passport?.passportNumber && (
                  <span className="error-msg">{errors.passport.passportNumber.message}</span>
                )}
              </FormItem>
              <FormItem>
                <Label>Expiry date*</Label>
                <VInput {...register('passport.expiryDate')} type="date" isValid={!errors.passport?.expiryDate} />
                {errors.passport?.expiryDate && <span className="error-msg">{errors.passport.expiryDate.message}</span>}
              </FormItem>
            </MultiColRow>
          </>
        )}
      </FormWrapper>
    </Modal>
  );
};

export default PersonModal;
